import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  MenuBook,
  MonetizationOn,
  Category,
  PlayArrow,
  Stop,
  Close,
  Search,
} from "@mui/icons-material";
import DonationsTable from "../stream-management/components/memorial-page/components/chema/DonationsTable";
import apiService, { BaseURL } from "../../service/Api";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../constants/permissions";

interface StreamResponse {
  id: number;
  title: string;
  date: string;
  booking: {
    id: number;
    title: string;
    deceased?: Array<{
      first_name: string;
      surname: string;
    }>;
  };
  go_fund_me_enabled: boolean;
  created_at: string;
}

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

interface DonationApiItem {
  id: number;
  reference: string;
  donor_name: string | null;
  display_name: string | null;
  email: string | null;
  phone: string | null;
  is_anonymous: boolean;
  amount: string;
  currency: string;
  payment_method: string | null;
  paid: boolean;
  created_at: string;
  stream_display_id?: number;
  stream_title?: string;
  type_of_donation_name?: string | null;
  type_of_donation?: number | null;
}

interface Fund {
  date: string;
  fundName: string;
  bookingTitle: string;
  streamTitle: string;
  totalRaised: number;
  totalRaisedDisplay: string;
  goFundMeEnabled: boolean;
  streamId: number;
  donors: string[];
  donationCount: number;
}

const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}/${month}/${day}`;
  } catch {
    return dateString;
  }
};

/** Strip the base URL so apiService.get receives a relative path */
const toRelativeUrl = (fullUrl: string): string => {
  if (fullUrl.startsWith(BaseURL)) {
    return fullUrl.slice(BaseURL.length);
  }
  // Handle when the URL starts with http but different origin
  try {
    const url = new URL(fullUrl);
    return url.pathname + url.search;
  } catch {
    return fullUrl;
  }
};

/** Fetch all pages of a paginated endpoint */
async function fetchAllPages<T>(
  endpoint: string,
  params?: Record<string, string | number | boolean>
): Promise<T[]> {
  const all: T[] = [];
  let nextUrl: string | null = endpoint;
  let isFirstPage = true;

  while (nextUrl) {
    const res: { data: PaginatedResponse<T> } = await apiService.get(
      nextUrl,
      isFirstPage ? params : undefined
    );

    const data: PaginatedResponse<T> = res.data;
    if (Array.isArray(data)) {
      all.push(...(data as unknown as T[]));
      break;
    }

    all.push(...(data.results || []));
    nextUrl = data.next ? toRelativeUrl(data.next) : null;
    isFirstPage = false;
  }

  return all;
}

const Insights = () => {
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<Fund[]>([]);
  const [allDonations, setAllDonations] = useState<DonationApiItem[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [donationsDialogOpen, setDonationsDialogOpen] = useState(false);
  const [selectedFund, setSelectedFund] = useState<Fund | null>(null);
  const [togglingStreamId, setTogglingStreamId] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all pages of streams and donations in parallel
      const [streams, donations] = await Promise.all([
        fetchAllPages<StreamResponse>("/api/streams/stream/", {
          go_fund_me_enabled: true,
        }),
        fetchAllPages<DonationApiItem>("/api/chema/donations/"),
      ]);

      setAllDonations(donations);

      // Group donations by stream_id
      const donationsByStream = new Map<
        number,
        { total: number; donors: string[]; count: number }
      >();
      for (const donation of donations) {
        const streamId = donation.stream_display_id;
        if (!streamId) continue;
        const entry = donationsByStream.get(streamId) || {
          total: 0,
          donors: [],
          count: 0,
        };
        entry.count++;
        if (donation.paid) {
          entry.total += parseFloat(donation.amount || "0");
        }
        const donorName = donation.display_name || donation.donor_name || "";
        if (
          donorName &&
          donorName !== "Anonymous" &&
          !entry.donors.includes(donorName)
        ) {
          entry.donors.push(donorName);
        }
        donationsByStream.set(streamId, entry);
      }

      // Transform streams to funds, sorted by total raised descending
      const fundsData: Fund[] = streams
        .map((stream: StreamResponse) => {
          const deceasedName = stream.booking?.deceased?.[0]
            ? `${stream.booking.deceased[0].first_name} ${stream.booking.deceased[0].surname}`
            : stream.title;

          const streamDonations = donationsByStream.get(stream.id) || {
            total: 0,
            donors: [],
            count: 0,
          };

          return {
            date: formatDate(stream.date || stream.created_at),
            fundName: deceasedName,
            bookingTitle: stream.booking?.title || "",
            streamTitle: stream.title || "",
            totalRaised: streamDonations.total,
            totalRaisedDisplay: `$${streamDonations.total.toFixed(2)}`,
            goFundMeEnabled: stream.go_fund_me_enabled,
            streamId: stream.id,
            donors: streamDonations.donors,
            donationCount: streamDonations.count,
          };
        })
        .sort((a: Fund, b: Fund) => b.totalRaised - a.totalRaised);

      setRows(fundsData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleGoFundMe = async (fund: Fund) => {
    if (!fund.streamId) return;
    const newEnabled = !fund.goFundMeEnabled;
    setTogglingStreamId(fund.streamId);
    try {
      await apiService.patch(`/api/streams/stream/${fund.streamId}/`, {
        go_fund_me_enabled: newEnabled,
      });
      setRows((prev) =>
        prev.map((r) =>
          r.streamId === fund.streamId
            ? { ...r, goFundMeEnabled: newEnabled }
            : r
        )
      );
    } catch (error) {
      console.error("Error toggling go_fund_me_enabled:", error);
    } finally {
      setTogglingStreamId(null);
    }
  };

  // Filter rows by search query
  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows;
    const q = searchQuery.toLowerCase();
    return rows.filter(
      (fund) =>
        fund.fundName.toLowerCase().includes(q) ||
        fund.bookingTitle.toLowerCase().includes(q) ||
        fund.streamTitle.toLowerCase().includes(q) ||
        fund.donors.some((d) => d.toLowerCase().includes(q))
    );
  }, [rows, searchQuery]);

  // Reset to first page when search changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  // Current page slice
  const paginatedRows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage]
  );

  // Compute stats from actual data
  const stats = useMemo(() => {
    const bookingsWithDonations = rows.filter(
      (r) => r.donationCount > 0
    ).length;
    const totalBookings = rows.length;
    const totalRaised = rows.reduce((sum, r) => sum + r.totalRaised, 0);

    // Donation types breakdown
    const typeCounts = new Map<string, number>();
    for (const donation of allDonations) {
      const typeName = donation.type_of_donation_name || "General";
      typeCounts.set(typeName, (typeCounts.get(typeName) || 0) + 1);
    }
    const typeEntries = Array.from(typeCounts.entries()).sort(
      (a, b) => b[1] - a[1]
    );
    const totalDonationCount = allDonations.length;
    const typesCount = typeCounts.size;

    return {
      bookings: {
        title: "Bookings with CHEMA",
        value: `${bookingsWithDonations}/${totalBookings}`,
        icon: <MenuBook sx={{ color: "#10b981" }} />,
        iconBg: "#ecfdf5",
      },
      totalRaised: {
        title: "Total CHEMA Raised",
        value: `$${totalRaised.toFixed(2)}`,
        icon: <MonetizationOn sx={{ color: "#f59e0b" }} />,
        iconBg: "#fffbeb",
      },
      donationTypes: {
        title: "Donation Types",
        value: `${typesCount} types`,
        subtitle: `${totalDonationCount} donations`,
        icon: <Category sx={{ color: "#3b82f6" }} />,
        iconBg: "#eff6ff",
        typeEntries,
      },
    };
  }, [rows, allDonations]);

  if (loading) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: "#f3f4f6", minHeight: "100vh" }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold" }}>
        CHEMA INSIGHTS
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Bookings with CHEMA */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 4,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              height: "100%",
            }}
          >
            <Box>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                {stats.bookings.title}
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {stats.bookings.value}
              </Typography>
            </Box>
            <Box
              sx={{
                bgcolor: stats.bookings.iconBg,
                p: 1.5,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {stats.bookings.icon}
            </Box>
          </Paper>
        </Grid>

        {/* Total CHEMA Raised */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 4,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              height: "100%",
            }}
          >
            <Box>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                {stats.totalRaised.title}
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {stats.totalRaised.value}
              </Typography>
            </Box>
            <Box
              sx={{
                bgcolor: stats.totalRaised.iconBg,
                p: 1.5,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {stats.totalRaised.icon}
            </Box>
          </Paper>
        </Grid>

        {/* Donation Types */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 4,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              height: "100%",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                {stats.donationTypes.title}
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {stats.donationTypes.value}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {stats.donationTypes.subtitle}
              </Typography>
              {stats.donationTypes.typeEntries.length > 0 && (
                <Box
                  sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}
                >
                  {stats.donationTypes.typeEntries.map(([name, count]) => (
                    <Chip
                      key={name}
                      label={`${name} (${count})`}
                      size="small"
                      sx={{
                        fontSize: "0.7rem",
                        height: 22,
                        bgcolor: "#eff6ff",
                        color: "#1e40af",
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
            <Box
              sx={{
                bgcolor: stats.donationTypes.iconBg,
                p: 1.5,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {stats.donationTypes.icon}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Funds Listing Table */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            CHEMA Funds Listing
          </Typography>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              size="small"
              placeholder="Search by stream, booking or donor..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "text.secondary", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                minWidth: 300,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
            <Button
              variant="outlined"
              disabled={!hasPermission(PERMISSIONS.DONATION_VIEW)}
              sx={{
                textTransform: "none",
                color: "text.primary",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              Generate Report
            </Button>
          </Box>
        </Box>

        <Table
          sx={{
            "& td, & th": {
              borderBottom: "none",
            },
          }}
        >
          <TableHead>
            <TableRow sx={{ bgcolor: "#f8f9fa" }}>
              <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Fund Name</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Donations</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Total Raised</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Manage</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {searchQuery
                      ? "No funds match your search"
                      : "No CHEMA funds found"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((fund) => (
                <TableRow
                  key={fund.streamId}
                  hover
                  sx={{
                    cursor: "pointer",
                    "&:hover": { bgcolor: "#f9fafb" },
                  }}
                  onClick={() => {
                    setSelectedFund(fund);
                    setDonationsDialogOpen(true);
                  }}
                >
                  {/* Date */}
                  <TableCell>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: fund.totalRaised > 0 ? "#10b981" : "#9ca3af",
                        }}
                      />
                      {fund.date}
                    </Box>
                  </TableCell>

                  {/* Fund Name */}
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      "&:hover": {
                        textDecoration: "underline",
                        color: "primary.main",
                      },
                    }}
                  >
                    {fund.fundName}
                  </TableCell>

                  {/* Donations Count */}
                  <TableCell>
                    <Chip
                      label={fund.donationCount}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        bgcolor:
                          fund.donationCount > 0 ? "#ecfdf5" : "#f3f4f6",
                        color: fund.donationCount > 0 ? "#065f46" : "#6b7280",
                      }}
                    />
                  </TableCell>

                  {/* Total Raised */}
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {fund.totalRaisedDisplay}
                  </TableCell>

                  {/* Status - Enabled/Disabled */}
                  <TableCell>
                    <Chip
                      label={fund.goFundMeEnabled ? "Enabled" : "Disabled"}
                      size="small"
                      sx={{
                        bgcolor: fund.goFundMeEnabled ? "#d1fae5" : "#fee2e2",
                        color: fund.goFundMeEnabled ? "#065f46" : "#991b1b",
                        fontWeight: 500,
                      }}
                    />
                  </TableCell>

                  {/* Manage - Toggle go_fund_me_enabled */}
                  <TableCell>
                    <Button
                      variant="outlined"
                      startIcon={
                        togglingStreamId === fund.streamId ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : fund.goFundMeEnabled ? (
                          <Stop />
                        ) : (
                          <PlayArrow />
                        )
                      }
                      size="small"
                      disabled={
                        !hasPermission(PERMISSIONS.DONATION_CHANGE) ||
                        togglingStreamId === fund.streamId
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleGoFundMe(fund);
                      }}
                      sx={{
                        textTransform: "none",
                        borderRadius: 4,
                        minWidth: 90,
                        borderColor: fund.goFundMeEnabled
                          ? "#ef4444"
                          : "#10b981",
                        color: fund.goFundMeEnabled ? "#ef4444" : "#10b981",
                        bgcolor: fund.goFundMeEnabled ? "#fef2f2" : "#ecfdf5",
                        "&:hover": {
                          borderColor: fund.goFundMeEnabled
                            ? "#dc2626"
                            : "#059669",
                          bgcolor: fund.goFundMeEnabled
                            ? "#fee2e2"
                            : "#d1fae5",
                        },
                      }}
                    >
                      {fund.goFundMeEnabled ? "Stop" : "Start"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={filteredRows.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Donations Dialog */}
      <Dialog
        open={donationsDialogOpen}
        onClose={() => setDonationsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              All Donations For {selectedFund?.fundName || ""}
            </Typography>
            <IconButton
              onClick={() => setDonationsDialogOpen(false)}
              size="small"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedFund?.streamId && (
            <DonationsTable
              streamId={selectedFund.streamId}
              title={`All Donations For ${selectedFund.fundName}`}
              ordering="-created_at"
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Insights;
